import React, { useState, useContext } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaPrint, FaFileExport, FaTimes, FaEye, FaUser, FaCalendarAlt, FaStethoscope, FaInfoCircle, FaCheckCircle, FaExpand } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import classes from "./DischargeModal.module.css";
import { printDischarge, exportDischargePDF } from "./DPrint";
import AuthContext from "../../../../store/auth-context";

const DischargeSummaryViewModal = ({ show, onHide, admission, hospitalData }) => {
    const { t } = useTranslation();
    const authCtx = useContext(AuthContext);
    const [isEnlarged, setIsEnlarged] = useState(false);
    if (!admission) return null;

    const calculateAge = (dob) => {
        if (!dob) return "";
        const birthDate = new Date(dob);
        if (isNaN(birthDate.getTime())) return "";
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const mapGender = (g) => {
        if (!g) return "-";
        const lowerG = String(g).toLowerCase();
        if (lowerG === "m" || lowerG === "male") return t("Male");
        if (lowerG === "f" || lowerG === "female") return t("Female");
        return g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        // If it's ISO or space separated, try to get just the date
        return dateStr.split(" ")[0];
    };

    const formatFullDateTime = (dateStr) => {
        if (!dateStr) return "-";
        return dateStr.replace("T", " ").substring(0, 19);
    };

    const formData = {
        patientName: `${admission.patientId?.firstName || ""} ${admission.patientId?.middleName || ""} ${admission.patientId?.lastName || ""}`.replace(/\s+/g, ' ').trim(),
        age: calculateAge(admission.patientId?.dob) || admission.patientId?.age || "-",
        sex: mapGender(admission.patientId?.gender),
        mobile: admission.patientId?.mobile || "-",
        weight: admission.weight || "-",
        doa: formatFullDateTime(admission.admissionDateTime),
        dod: formatFullDateTime(admission.dischargeDateTime),
        ipdNo: admission.ipdAdmissionId,
        regNo: admission.patientId?.registrationNo || "-",
        address: admission.patientId?.address || "-",
        city: admission.patientId?.city || "",
        pincode: admission.patientId?.pincode || "",
        diagnosis: admission.diagnosis || admission.admissionReason || "No Diagnosis Recorded",
        dischargeType: admission.dischargeType || "-",
        condition: admission.conditionAtDischarge || "Stable",
        drWrittenDischargeSummery: admission.drWrittenDischargeSummery || "",
        pho: admission.pho || "-",
        co: admission.co || "-",
        kco: admission.kco || "-",
        cf: admission.cf || "-",
        nextAppointment: admission.nextAppointmentDate || admission.nextFollowupVisitToDr || "-",
        notes: admission.notes || "-",
        medicoLegal: admission.option2 || "-",
        referredBy: admission.patientId?.referredBy || "-",
        symptoms: `${admission.symptomsTitle || ""} ${admission.symptomsDesc || ""}`.trim() || t("No symptoms described"),
        wardBed: admission.wardBed ? `${admission.wardBed.wardName} - ${admission.wardBed.bedNumber}` : "-",
        floor: admission.wardBed?.floor || "-",
        doctor: `${t("Dr.")} ${admission.doctorId?.firstName || ""} ${admission.doctorId?.middleName || ""} ${admission.doctorId?.lastName || ""}${admission.doctorId?.degree ? ` (${admission.doctorId.degree})` : ""}`.replace(/\s+/g, ' ').trim(),
        vitals: {
            bp: admission.bp || "-",
            pulse: admission.pulse || "-",
            temp: admission.temperature || "-",
            spo2: admission.spo2 || "-",
            wt: (admission.weight || "-") + " kg"
        },
        bloodGroup: admission.patientId?.bloodGroup || admission.bloodGroup || "-",
        ids: {
            adhar: admission.adharCardNo || admission.patientId?.adharCardNo || "-",
            pan: admission.panCardNo || admission.patientId?.panCardNo || "-",
            insurance: admission.insuranceCardNo || "-"
        }
    };

    const InfoRow = ({ label, value, icon: Icon, color = "text-muted" }) => (
        <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom border-light">
            <span className={`${color} small d-flex align-items-center gap-2`}>
                {Icon && <Icon size={12} />} {label}
            </span>
            <span className="fw-bold text-dark small text-end">{value}</span>
        </div>
    );

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable className="shadow-premium">
            <div className="modal-content border-0 overflow-hidden rounded-4">
                <div className="modal-header bg-premium text-white py-3">
                    <h5 className="modal-title d-flex align-items-center">
                        <FaEye className="me-2" /> {t("Discharged Patient Details")} - {formData.patientName}
                    </h5>
                    <button type="button" className="btn-close btn-close-white" onClick={onHide} aria-label={t("Close")}></button>
                </div>

                <Modal.Body className="p-4 bg-light">
                    <div className="row g-4">
                        {/* 1. Patient Profile Section */}
                        <div className="col-lg-4">
                            <div className="card border-0 shadow-sm h-100 rounded-4 overflow-hidden">
                                <div className="card-header bg-premium text-white py-3">
                                    <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                        <FaUser /> {t("Patient Profile")}
                                    </h6>
                                </div>
                                <div className="card-body p-4">
                                    <div className="text-center mb-4">
                                        <div className="avatar-circle mx-auto mb-3 bg-premium text-white d-flex align-items-center justify-content-center fw-bold shadow-sm" style={{ width: '80px', height: '80px', borderRadius: '50%', fontSize: '32px' }}>
                                            {admission.patientId?.firstName?.charAt(0)}{admission.patientId?.lastName?.charAt(0)}
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">{formData.patientName}</h5>
                                        <span className="text-muted small">{t("PID")}: {admission.patientId?.patientId}</span>
                                    </div>

                                    <div className="mt-4 pt-2">
                                        <InfoRow label={t("Registration No.")} value={formData.regNo} color="text-teal" />
                                        <InfoRow label={t("Gender")} value={formData.sex} />
                                        <InfoRow label={t("Age")} value={`${formData.age} ${t("Yrs")}`} />
                                        <InfoRow label={t("Weight")} value={formData.weight && formData.weight !== "-" ? `${formData.weight} kg` : "-"} />
                                        <InfoRow label={t("Mobile")} value={formData.mobile} color="text-teal" />
                                        <InfoRow label={t("Blood Group")} value={formData.bloodGroup} />
                                        <InfoRow label={t("Referred By")} value={formData.referredBy} />
                                        <div className="mb-3">
                                            <label className="text-muted small d-block mb-1">{t("Address")}</label>
                                            <div className="p-2 bg-light rounded small text-dark border-start border-3 border-teal">
                                                {formData.address}
                                                {(formData.city || formData.pincode) && ` (${formData.city}${formData.city && formData.pincode ? ', ' : ''}${formData.pincode})`}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Details Section */}
                        <div className="col-lg-8">
                            {/* Admission Status Cards */}
                            <div className="row g-3 mb-4">
                                <div className="col-md-3">
                                    <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100">
                                        <label className="text-muted small d-block mb-1">{t("IPD Number")}</label>
                                        <h5 className="mb-0 fw-bold text-dark">{formData.ipdNo}</h5>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100">
                                        <label className="text-muted small d-block mb-1">{t("Ward / Bed")}</label>
                                        <h6 className="mb-0 fw-bold text-dark">{formData.wardBed}</h6>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100">
                                        <label className="text-muted small d-block mb-1">{t("Room Floor")}</label>
                                        <h6 className="mb-0 fw-bold text-dark">{formData.floor}</h6>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="p-3 bg-white rounded-4 border-start border-4 border-teal shadow-sm h-100">
                                        <label className="text-muted small d-block mb-1">{t("Status")}</label>
                                        <h6 className="mb-0 fw-bold text-teal">{t(admission.status)}</h6>
                                    </div>
                                </div>
                            </div>

                            {/* Info Table Card */}
                            <div className="card border-0 shadow-sm rounded-4 mb-4">
                                <div className="card-body p-4">
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <h6 className="fw-bold text-teal border-bottom pb-2 mb-3 d-flex align-items-center gap-2">
                                                <FaCalendarAlt /> {t("Admission Info")}
                                            </h6>
                                            <InfoRow label={t("Date & Time")} value={formData.doa} />
                                            <InfoRow label={t("Consulting Dr.")} value={formData.doctor} />
                                            <div className="mt-2">
                                                <label className="text-muted small d-block mb-1">{t("Reason for Admission")}</label>
                                                <p className="small text-dark mb-0">{admission.admissionReason || "-"}</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <h6 className="fw-bold text-teal border-bottom pb-2 mb-3 d-flex align-items-center gap-2">
                                                <FaInfoCircle /> {t("Guardian Details")}
                                            </h6>
                                            <InfoRow label={t("Name")} value={admission.guardianName || "-"} />
                                            <InfoRow label={t("Relation")} value={t(admission.guardianRelation) || "-"} />
                                            <InfoRow label={t("Mobile")} value={admission.guardianContactNumber || "-"} />
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <h6 className="fw-bold text-teal border-bottom pb-2 mb-3 d-flex align-items-center gap-2">
                                            <FaStethoscope /> {t("Clinical Data")}
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label className="text-muted small d-block mb-1">{t("Diagnosis")}</label>
                                                <div className="p-2 rounded bg-teal text-white small fw-bold text-center shadow-sm">
                                                    {formData.diagnosis}
                                                </div>
                                            </div>
                                            <div className="col-md-8">
                                                <label className="text-muted small d-block mb-1">{t("Symptoms")} ({t(admission.symptomsTitle) || t('General')})</label>
                                                <div className="p-2 rounded bg-light small text-dark border">
                                                    {formData.symptoms}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Added Clinical History Sections */}
                                        <div className="row g-3 mt-1">
                                            <div className="col-md-6">
                                                <label className="text-muted small d-block mb-1">{t("Personal History")}</label>
                                                <div className="p-2 rounded bg-white small text-dark border-start border-3 border-teal shadow-sm">
                                                    {formData.pho}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small d-block mb-1">{t("Complaint Of")}</label>
                                                <div className="p-2 rounded bg-white small text-dark border-start border-3 border-teal shadow-sm">
                                                    {formData.co}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row g-3 mt-1">
                                            <div className="col-md-6">
                                                <label className="text-muted small d-block mb-1">{t("Known Case Of")}</label>
                                                <div className="p-2 rounded bg-white small text-dark border-start border-3 border-teal shadow-sm">
                                                    {formData.kco}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small d-block mb-1">{t("Clinical Findings")}</label>
                                                <div className="p-2 rounded bg-white small text-dark border-start border-3 border-teal shadow-sm">
                                                    {formData.cf}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Vitals & ID Cards Highlights */}
                                    <div className="row g-3 mt-1">
                                        <div className="col-md-6">
                                            <div className="p-3 bg-light rounded-4 border border-teal-subtle h-100">
                                                <h6 className="fw-bold text-teal small mb-3 border-bottom pb-1">{t("Patient Vitals")}</h6>
                                                <div className="row text-center g-2">
                                                    <div className="col">
                                                        <label className="text-muted d-block small mb-0">{t("BP")}</label>
                                                        <span className="fw-bold text-teal small">{formData.vitals.bp}</span>
                                                    </div>
                                                    <div className="col">
                                                        <label className="text-muted d-block small mb-0">{t("Pulse")}</label>
                                                        <span className="fw-bold text-teal small">{formData.vitals.pulse}</span>
                                                    </div>
                                                    <div className="col">
                                                        <label className="text-muted d-block small mb-0">{t("Temp")}</label>
                                                        <span className="fw-bold text-teal small">{formData.vitals.temp}°F</span>
                                                    </div>
                                                    <div className="col">
                                                        <label className="text-muted d-block small mb-0">{t("SpO2")}</label>
                                                        <span className="fw-bold text-teal small">{formData.vitals.spo2}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="p-3 bg-white rounded-4 border border-info-subtle h-100 shadow-sm">
                                                <h6 className="fw-bold text-info small mb-3 border-bottom pb-1">{t("Identification & Insurance")}</h6>
                                                <InfoRow label={t("Adhar Card")} value={formData.ids.adhar} />
                                                <InfoRow label={t("PAN Card")} value={formData.ids.pan} />
                                                <InfoRow label={t("Insurance No.")} value={formData.ids.insurance} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Discharge Information Card */}
                            <div className="card border-0 shadow-sm rounded-4 border-top border-5 border-teal">
                                <div className="card-header bg-white py-3">
                                    <h6 className="mb-0 fw-bold text-teal d-flex align-items-center gap-2">
                                        <FaCheckCircle /> {t("Discharge Summary")}
                                    </h6>
                                </div>
                                <div className="card-body p-4">
                                    <div className="row g-4 mb-4">
                                        <div className="col-md-6">
                                            <InfoRow label={t("Discharge Date")} value={formData.dod} icon={FaCalendarAlt} color="text-danger" />
                                            <InfoRow label={t("Discharge Type")} value={t(formData.dischargeType)} />
                                        </div>
                                        <div className="col-md-6">
                                            <InfoRow label={t("Condition")} value={formData.condition} />
                                            <InfoRow label={t("Follow-up Date")} value={formData.nextAppointment} color="text-primary" />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <label className="text-teal fw-bold small mb-0">{t("Doctor's Clinical Summary & Treatment")}</label>
                                            <Button
                                                variant="link"
                                                className="p-0 d-flex align-items-center gap-1"
                                                onClick={() => setIsEnlarged(true)}
                                                style={{
                                                    textDecoration: 'none',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    backgroundColor: 'var(--primary-color)',
                                                    padding: '4px 12px',
                                                    borderRadius: '20px',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }}
                                            >
                                                <FaExpand size={12} /> {t("Enlarge View")}
                                            </Button>
                                        </div>
                                        <div
                                            className="p-3 bg-white rounded border-teal shadow-sm overflow-auto"
                                            style={{ minHeight: "150px", maxHeight: "400px", borderLeft: "4px solid var(--primary-color)" }}
                                            dangerouslySetInnerHTML={{ __html: formData.drWrittenDischargeSummery || `<p class='text-muted fst-italic'>${t("No detailed discharge summary recorded.")}</p>` }}
                                        />
                                    </div>

                                    {formData.notes && (
                                        <div className="bg-light p-3 rounded-3 border-start border-4 border-secondary">
                                            <label className="text-muted small d-block mb-1 fw-bold">{t("Additional Notes:")}</label>
                                            <p className="small mb-0 text-dark">{formData.notes}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="bg-white p-3 border-top">
                    <div className="d-flex w-100 justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                            <Button
                                className="btn-premium"
                                onClick={() => printDischarge(formData, authCtx, "a4")}
                            >
                                <FaPrint className="me-2" /> {t("PRINT SUMMARY")}
                            </Button>
                            <Button
                                className="btn-export-premium"
                                onClick={() => exportDischargePDF(formData, authCtx)}
                            >
                                <FaFileExport className="me-2" /> {t("EXPORT PDF")}
                            </Button>
                        </div>
                        <Button className="btn-secondary-premium shadow-sm px-4" onClick={onHide}>
                            <FaTimes className="me-2" /> {t("CLOSE DETAILS")}
                        </Button>
                    </div>
                </Modal.Footer>
            </div>

            {/* Enlarged View Modal */}
            <Modal show={isEnlarged} onHide={() => setIsEnlarged(false)} fullscreen={true} centered scrollable>
                <Modal.Header closeButton className="bg-premium text-white py-3">
                    <Modal.Title className="d-flex align-items-center">
                        <FaStethoscope className="me-2" /> {t("Clinical Summary & Treatment")} - {formData.patientName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 p-md-5 bg-light">
                    <div
                        className="p-5 bg-white shadow-lg rounded-4 overflow-auto mx-auto"
                        style={{ maxWidth: '1000px', minHeight: '100%', borderLeft: "6px solid var(--primary-color)" }}
                        dangerouslySetInnerHTML={{ __html: formData.drWrittenDischargeSummery || `<p class='text-muted fst-italic'>${t("No detailed discharge summary recorded.")}</p>` }}
                    />
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <Button className="btn-secondary-premium shadow-sm px-4" onClick={() => setIsEnlarged(false)}>
                        <FaTimes className="me-2" /> {t("CLOSE FULL SCREEN")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
};

export default DischargeSummaryViewModal;
